import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { hasPermission, hasAnyRole } from '../utils/permissions'
import { 
  LayoutDashboard, 
  Users, 
  School, 
  Bell, 
  FileText, 
  LogOut,
  X,
  Building2,
  Tag,
  Activity
} from 'lucide-react'
import { useState, useEffect } from 'react'

export const Sidebar = ({ isOpen, onClose }) => {
  const { userProfile, signOut, stopImpersonating } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Impersonate durumunu kontrol et (hooks her zaman en üstte olmalı)
  const [isImpersonating, setIsImpersonating] = useState(false)

  useEffect(() => {
    const checkImpersonating = () => {
      const storedUser = localStorage.getItem('meb_auth_user')
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser)
          const impersonating = !!(userData.impersonated && userData.originalUser)
          setIsImpersonating(impersonating)
        } catch (e) {
          setIsImpersonating(false)
        }
      } else {
        setIsImpersonating(false)
      }
    }

    checkImpersonating()
    
    // Custom event listener
    const handleStorageChange = () => {
      checkImpersonating()
    }
    
    window.addEventListener('localStorageChange', handleStorageChange)
    window.addEventListener('storage', handleStorageChange)
    
    // Interval kaldırıldı - sadece event listener kullanıyoruz (sürekli yenilemeyi önlemek için)
    
    return () => {
      window.removeEventListener('localStorageChange', handleStorageChange)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  // Conditional return hooks'lardan sonra olmalı
  if (!userProfile) return null

  const isAdmin = hasAnyRole(userProfile, ['super_admin', 'admin'])
  const isMudur = hasAnyRole(userProfile, ['mudur'])

  const handleStopImpersonating = async () => {
    try {
      console.log('Sidebar - Stop impersonating başlatılıyor...')
      await stopImpersonating()
      console.log('Sidebar - Stop impersonating başarılı, yönlendiriliyor...')
      // Sadece navigate, reload yok (state zaten güncellendi)
      navigate('/admin/users')
    } catch (error) {
      console.error('Sidebar - Stop impersonating hatası:', error)
      alert('Yönetici arayüzüne dönüş hatası: ' + (error.message || 'Bilinmeyen hata'))
    }
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  const menuItems = [
    {
      name: 'Ana Sayfa',
      path: '/',
      icon: LayoutDashboard,
      show: true,
    },
    ...(isAdmin
      ? [
          {
            name: 'Kullanıcılar',
            path: '/admin/users',
            icon: Users,
            show: true,
          },
          {
            name: 'Okullar',
            path: '/admin/schools',
            icon: School,
            show: true,
          },
          {
            name: 'Okul Kategorileri',
            path: '/admin/school-categories',
            icon: Building2,
            show: true,
          },
          {
            name: 'Bildirim Kategorileri',
            path: '/admin/notification-categories',
            icon: Tag,
            show: true,
          },
          {
            name: 'Bildirim Geçmişi',
            path: '/admin/notification-history',
            icon: FileText,
            show: true,
          },
        ]
      : []),
    ...(hasPermission(userProfile, 'notifications.create')
      ? [
          {
            name: 'Bildirim Gönder',
            path: '/notifications/create',
            icon: Bell,
            show: true,
          },
        ]
      : []),
    ...(isMudur
      ? [
          {
            name: 'Bildirimlerim',
            path: '/mudur/notifications',
            icon: FileText,
            show: true,
          },
        ]
      : []),
    {
      name: 'Loglar',
      path: '/logs',
      icon: Activity,
      show: true,
    },
  ]

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(path)
  }

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-50 h-full
          bg-gray-900 text-white
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          w-64 shadow-2xl
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-800">
            <h1 className="text-xl font-bold text-white">
              MEB Bildirim
            </h1>
            <button
              onClick={onClose}
              className="lg:hidden text-gray-400 hover:text-white"
            >
              <X size={24} />
            </button>
          </div>

          {/* User Info */}
          <div className="p-6 border-b border-gray-800">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">
                  {userProfile.full_name?.[0]?.toUpperCase() || userProfile.email[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {userProfile.full_name || userProfile.email}
                </p>
                <p className="text-xs text-gray-400 truncate">
                  {userProfile.role?.display_name || 'Kullanıcı'}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon
              const active = isActive(item.path)
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg
                    transition-colors duration-200
                    ${
                      active
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }
                  `}
                >
                  <Icon size={20} />
                  <span className="font-medium">{item.name}</span>
                </Link>
              )
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-800 space-y-2">
            {/* Impersonate durumu göster */}
            {isImpersonating && (() => {
              const storedUser = localStorage.getItem('meb_auth_user')
              try {
                const userData = JSON.parse(storedUser)
                return (
                  <div className="mb-2 space-y-2">
                    <div className="p-2 bg-yellow-900 bg-opacity-50 rounded-lg text-xs text-yellow-200">
                      <p className="font-semibold">Başka kullanıcı olarak giriş yapıldı</p>
                      <p className="text-yellow-300 mt-1">
                        Orijinal: {userData.originalUser?.email}
                      </p>
                    </div>
                    <button
                      onClick={handleStopImpersonating}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg transition-all duration-200 text-sm font-semibold"
                    >
                      <LogOut size={18} />
                      <span>Yönetici Arayüzüne Dön</span>
                    </button>
                  </div>
                )
              } catch (e) {
                return null
              }
            })()}
            
            <button
              onClick={handleSignOut}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <LogOut size={20} />
              <span className="font-medium">Çıkış Yap</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}

